import React, { useState, useCallback, useMemo } from "react";
import { Alert, Badge, Container } from "react-bootstrap";
import { useParams } from "react-router-dom";
import InfoPopover from "@core/components/InfoPopover";
import PageBreadcrumb from "@core/components/PageBreadcrumb";
import DeadlineStatusBanner from "@core/reports/components/DeadlineStatusBanner";
import { ActivityEntryForm } from "@features/report/activity-entry/components/ActivityEntryForm";
import { getWorkflowStatusLabel } from "@core/reports/utils/reportWorkflow";
import { subtleBadgeProps } from "@core/utils/subtleBadge";
import { useEntityBasePath } from "@core/hooks/useEntityBasePath";
import { activitySubmissionsToEntryTrail } from "@core/navigation/pageBreadcrumbTrails";
import {
    areActivityEntryHeaderContextsEqual,
    type ActivityEntryHeaderContext,
} from "@features/report/activity-entry/utils/activityEntryHeaderContext";
import {
    formatReportTagDeadline,
    isActivityEntryDeadlineComplete,
    shouldShowDeadlineUrgencyBanner,
} from "@features/report/tag/utils/reportTagDeadline";

const ManageDepartmentSubmissions: React.FC = () => {
    const { churchId: urlChurchId, districtId: urlDistrictId, nationId: urlNationId } =
        useParams<{ churchId?: string; districtId?: string; nationId?: string }>();
    const [headerContext, setHeaderContext] = useState<ActivityEntryHeaderContext | null>(null);
    const basePath = useEntityBasePath();

    const scope = useMemo(() => {
        if (urlChurchId) return { type: "church" as const, id: Number(urlChurchId) };
        if (urlDistrictId) return { type: "district" as const, id: Number(urlDistrictId) };
        if (urlNationId) return { type: "nation" as const, id: Number(urlNationId) };
        return null;
    }, [urlChurchId, urlDistrictId, urlNationId]);

    const breadcrumbItems = useMemo(
        () =>
            basePath && headerContext?.templateName
                ? activitySubmissionsToEntryTrail(basePath, headerContext.templateName)
                : [],
        [basePath, headerContext?.templateName]
    );

    const handleHeaderContextChange = useCallback((ctx: ActivityEntryHeaderContext | null) => {
        setHeaderContext((prev) =>
            areActivityEntryHeaderContextsEqual(prev, ctx) ? prev : ctx
        );
    }, []);

    const deadlineLabel = formatReportTagDeadline(headerContext?.reportTagDeadline);
    const showUrgencyBanner = shouldShowDeadlineUrgencyBanner({
        tagIsOpen: headerContext?.tagIsOpen !== false,
        deadline: headerContext?.reportTagDeadline,
        isComplete: isActivityEntryDeadlineComplete(headerContext?.activityStatusName),
    });

    return (
        <Container fluid className="py-4">
            <PageBreadcrumb items={breadcrumbItems} />
            <div className="ntcog-form-wrap mt-0">
                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div className="ntcog-page-title-block mb-0">
                        {headerContext ? (
                            <>
                                <h1 className="ntcog-page-heading mb-1">{headerContext.templateName}</h1>
                                <div className="text-muted mb-0" style={{ fontSize: "1rem", lineHeight: 1.6 }}>
                                    <div>
                                        <span className="fw-semibold">Department:</span>{" "}
                                        {headerContext.departmentName}
                                    </div>
                                    {headerContext.reportTagName ? (
                                        <div>
                                            <span className="fw-semibold">Reporting Period:</span>{" "}
                                            {headerContext.reportTagName}
                                            {headerContext.reportTagStatusLabel
                                                ? ` · ${headerContext.reportTagStatusLabel}`
                                                : ""}
                                            {deadlineLabel ? ` · Deadline: ${deadlineLabel}` : ""}
                                        </div>
                                    ) : null}
                                </div>
                            </>
                        ) : (
                            <>
                                <h1 className="ntcog-page-heading mb-1">Submit Your Activity Submission</h1>
                                <p className="text-muted mb-0" style={{ fontSize: "1rem", lineHeight: 1.6 }}>
                                    Select a department, report template, and reporting period to open the entry form.
                                </p>
                            </>
                        )}
                    </div>

                    {headerContext ? (
                        <div className="d-flex flex-column align-items-end gap-2 flex-shrink-0">
                            <div
                                className="d-flex align-items-center gap-2"
                                aria-label={`Status: ${headerContext.activityStatusLabel}`}
                            >
                                <span className="text-muted small">Status:</span>
                                <Badge {...subtleBadgeProps(headerContext.activityStatusVariant)}>
                                    {headerContext.activityStatusLabel}
                                </Badge>
                            </div>
                            {headerContext.accessLabel === "View only" ? (
                                <InfoPopover
                                    title="View only"
                                    text={headerContext.editabilityText}
                                    placement="bottom"
                                    id="activity-entry-view-only-reason"
                                />
                            ) : null}
                        </div>
                    ) : null}
                </div>

                {headerContext?.needsAttentionReason ? (
                    <Alert variant="warning" className="mt-2 mb-0 py-2 px-3 small">
                        <div className="fw-semibold mb-1">{getWorkflowStatusLabel("NEEDS_ATTENTION")}</div>
                        <div>{headerContext.needsAttentionReason}</div>
                    </Alert>
                ) : null}

                {showUrgencyBanner ? (
                    <DeadlineStatusBanner
                        className="mt-2 mb-0"
                        since={headerContext?.reportTagSince}
                        until={headerContext?.reportTagUntil}
                        deadline={headerContext?.reportTagDeadline}
                        tagIsOpen={headerContext?.tagIsOpen !== false}
                    />
                ) : null}
            </div>
            <ActivityEntryForm scope={scope} onHeaderContextChange={handleHeaderContextChange} />
        </Container>
    );
};

export default ManageDepartmentSubmissions;
